// Certificate contract shared by the API and the web app (pilot slice 9, ADR-0030, ADR-0035).
// Wording rules (AGENTS.md): a nikah certificate supports, and never replaces, local-body marriage
// registration; a death and burial record is not the civil death certificate. Unpaid varisankhya never
// blocks a certificate: arrears are shown as information only.
import type { Role } from './auth';
import type { SequenceCode } from './sequences';

export const CERT_TYPES = ['MEMBERSHIP', 'NIKAH_NOC', 'DEATH_BURIAL'] as const;
export type CertType = (typeof CERT_TYPES)[number];

/** The numbering series each type takes its serial from (Sequence Master). */
export const CERT_SERIES: Record<CertType, SequenceCode> = { MEMBERSHIP: 'CERT_MEMBERSHIP', NIKAH_NOC: 'CERT_NIKAH_NOC', DEATH_BURIAL: 'CERT_DEATH' };

/** Who may record a request, reject it and withdraw an issued certificate. */
export const CERT_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];
/** Who may sign. Two different people must sign before a certificate is issued. */
export const SIGNER_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];
export const SIGNATURES_NEEDED = 2;

/** Minimum ages for marriage in India (Prohibition of Child Marriage Act): a NOC is refused below them. */
export const MARRIAGE_AGE = { M: 21, F: 18 } as const;

export const REQUEST_STATES = ['REQUESTED', 'ISSUED', 'REJECTED'] as const;
export type RequestState = (typeof REQUEST_STATES)[number];
export const CERT_STATES = ['ISSUED', 'WITHDRAWN'] as const;
export type CertState = (typeof CERT_STATES)[number];
export const PDF_STATES = ['PENDING', 'READY', 'FAILED'] as const;
export type PdfState = (typeof PDF_STATES)[number];

export const CERT_LIMITS = { text: 120, note: 300 } as const;

export interface MembershipDetails {
  purpose: string;
}
export interface NikahNocDetails {
  spouseName: string;
  spouseMahallu: string;
  /** "2026-12-20", optional. */
  nikahDate: string | null;
}
export interface DeathBurialDetails {
  dateOfDeath: string;
  placeOfDeath: string | null;
  burialDate: string;
  burialPlace: string;
}
export type CertDetails = MembershipDetails | NikahNocDetails | DeathBurialDetails;

export interface CertRequestInput {
  type: CertType;
  memberId: string;
  details: CertDetails;
  note?: string | null;
}

export interface SignatureRow {
  signerName: string;
  signerRole: Role;
  signedAt: string;
  /** First 12 hex characters of the SHA-256 of the content that was signed. */
  contentHash: string;
}

export interface CertSubject {
  memberId: string;
  nameEn: string;
  nameMl: string | null;
  relation: string;
  birthYear: number | null;
  gender: 'M' | 'F' | null;
  status: string;
  house: { id: string; houseNo: string; nameEn: string; nameMl: string | null; ward: string };
}

export interface CertRequestRow {
  id: string;
  type: CertType;
  state: RequestState;
  subject: CertSubject;
  details: CertDetails;
  note: string | null;
  /** Information only: never a reason to refuse (Waqf Board circular). */
  houseArrearsInr: number;
  signatures: SignatureRow[];
  requestedBy: string;
  requestedAt: string;
  rejectReason: string | null;
  certificateId: string | null;
  /** True when the signed-in person may sign it now. */
  canSign: boolean;
  version: number;
}

export interface CertificateRow {
  id: string;
  serial: string;
  type: CertType;
  state: CertState;
  subject: { nameEn: string; nameMl: string | null; houseNo: string };
  issuedAt: string;
  pdf: { state: PdfState; sha256: string | null; error: string | null };
  withdrawnAt: string | null;
  withdrawReason: string | null;
  version: number;
}

/** What the public QR page shows: enough to confirm the certificate is genuine, nothing more. */
export interface PublicCertificate {
  mahallu: { nameEn: string; nameMl: string | null };
  type: CertType;
  serial: string;
  issuedOn: string;
  /** The person's name, so the reader can match it with the paper in hand. */
  nameEn: string;
  nameMl: string | null;
  state: CertState;
  withdrawnOn: string | null;
  pdfSha256: string | null;
}
